import clsx from 'clsx';
import isEmpty from 'lodash/isEmpty';
import { useState } from 'react';

import { Input } from '../../../../../../common/_custom';
import { GREEN_BG_WHITE_TICK_CIRCLE, VERIFY_GREY_CIRCLE, VERIFY_PURPLE_CIRCLE, verifyOrange } from '../../../../../../assets/hostedassets';

import styles from '../DirectorDetails.module.scss';
import LoadingSpinner from '../../../../../../common/_custom/LoadingSpinner/LoadingSpinner';
import FetchFillIcon from '../../../../../../assets/Svg/FetchFilIcon';
import { ONE_DAY_IN_MILLISECONDS } from '../../../../../../utils/constants/commonConstants';
import { creditBureauConfig } from '../../../../../../store/investor/transformer';

const renderAsset = (asset: string | React.ReactNode) => {
    if (typeof asset === 'string') {
        return <img src={asset} alt="" className={styles.VerifyIcon} />;
    }
    return asset;
};

interface ConsentStatus {
    [key: string]: {
        bureauName: string;
        lastFetchedAt: Date;
        isConsentGiven: boolean;
        isBureauError?: boolean;
    };
}
interface DirectorDetails {
    directorName: string;
    phoneNo: string;
    consentStatus?: ConsentStatus;
    lastInviteSentAt?: string;
    isNewDir?: boolean;
    updatedAt?: number;
    cibilConsentStatus?: ConsentStatus;
    pan?: string;
}

const isTimeStampOneDayOld = (timestamp: number | Date | undefined) => {
    if (!timestamp) return true;
    if (timestamp instanceof Date) {
        timestamp = timestamp.getTime();
    }
    const currentTime = new Date().getTime();
    const diff = currentTime - timestamp;
    const val = diff > ONE_DAY_IN_MILLISECONDS;

    return val;
};

interface DirectorInputFieldProps {
    director: DirectorDetails;
    index: number;
    handleChange: (e: string, index: number, name: string) => void;
    sendLink: (
        name: string,
        setDisableSendLink: (value: boolean) => void,
        input: number,
        source: string,
        flowType?: string,
        phoneNo?: string,
        pan?: string,
    ) => void;
    errorMessage: string;
    isLoading?: boolean;
    isError: boolean;
    isInvestorSide?: boolean;
    placeHolder?: string;
    creditBureauConfig?: any;
    validateInput(value: string, name: string): boolean;
}

function DirectorInputField({
    director,
    index,
    handleChange,
    sendLink,
    errorMessage,
    isLoading,
    isError,
    isInvestorSide,
    placeHolder,
    creditBureauConfig,
    validateInput,
}: DirectorInputFieldProps) {
    const { FormContainer, Field, DisabledBannerText, BannerTextVerified, BannerText } = styles;

    const [disableSendLink, setDisableSendLink] = useState<boolean>(true);

    // Timestamp of the last successful credit bureau data fetch for this director.
    const lastFetchedAt = director?.consentStatus?.[creditBureauConfig?.name]?.lastFetchedAt;
    const reFetchSoftPull = isTimeStampOneDayOld(lastFetchedAt) && isInvestorSide;

    const handleSendLinkClick = () => {
        sendLink(director.directorName, setDisableSendLink, index, creditBureauConfig?.name, undefined, director.phoneNo, director.pan);
    };
    const isConsentGiven = director?.consentStatus?.[creditBureauConfig?.name]?.isConsentGiven;

    const renderFetchButton = () => {
        if (isLoading) {
            return <LoadingSpinner color="var(--primary-text-colour)" />;
        }
        return (
            <div className={styles.VerifyImg}>
                {renderAsset(
                    !validateInput(director[creditBureauConfig?.dataToDisplay], creditBureauConfig?.dataToDisplay) ? (
                        VERIFY_GREY_CIRCLE
                    ) : (
                        <FetchFillIcon colour="var(--primary-text-colour)" height="15" width="15" className={styles.VerifyIcon} />
                    ),
                )}
                <span>{isConsentGiven ? 'Refetch' : 'Fetch'}</span>
            </div>
        );
    };

    const renderConsentState = () => {
        //refetch button for PNO when time is more than 24 hours
        const hasBureauError = director?.consentStatus?.[creditBureauConfig?.name]?.isBureauError;
        if (isConsentGiven || hasBureauError) {
            return reFetchSoftPull || hasBureauError ? (
                <div
                    onClick={handleSendLinkClick}
                    className={clsx(BannerText, {
                        [DisabledBannerText]:
                            !disableSendLink ||
                            !validateInput(director[creditBureauConfig?.dataToDisplay], creditBureauConfig?.dataToDisplay),
                    })}
                >
                    {renderFetchButton()}
                </div>
            ) : (
                <div className={BannerTextVerified}>
                    {renderAsset(GREEN_BG_WHITE_TICK_CIRCLE)}
                    <span>Fetched</span>
                </div>
            );
        }
        // Initial fetch button for non-consent state
        return (
            <div
                onClick={handleSendLinkClick}
                className={clsx(BannerText, {
                    [DisabledBannerText]:
                        !disableSendLink || !validateInput(director[creditBureauConfig?.dataToDisplay], creditBureauConfig?.dataToDisplay),
                })}
            >
                {renderFetchButton()}
            </div>
        );
    };

    const isInputDisabled = () => {
        return (
            director?.cibilConsentStatus?.[creditBureauConfig?.name]?.isConsentGiven &&
            validateInput(director[creditBureauConfig?.dataToDisplay], creditBureauConfig?.dataToDisplay)
        );
    };

    return (
        <div key={index} className={styles.dflex}>
            <div style={{ width: '40%' }} className={styles.FormContainer}>
                <div className={styles.Field}>{director.directorName}</div>
            </div>
            <div style={{ width: '60%' }} className={FormContainer}>
                <div className={Field}>
                    <Input
                        name={creditBureauConfig?.dataToDisplay}
                        type="text"
                        style={{ margin: 0 }}
                        inputStyle={{
                            backgroundColor: isConsentGiven ? '#ffffff' : '#f9f9f9',
                            borderColor: isConsentGiven ? '#D9D9D9' : 'transparent',
                            borderWidth: '0.5px',
                            borderStyle: 'solid',
                        }}
                        value={director[creditBureauConfig?.dataToDisplay]}
                        onChange={(e) => handleChange(e, index, creditBureauConfig?.dataToDisplay)}
                        disabled={isInputDisabled()}
                        placeholder={placeHolder}
                        errorMessage={errorMessage}
                        isValid={!isError}
                    />
                    {director[creditBureauConfig?.dataToDisplay] && renderConsentState()}
                </div>
            </div>
        </div>
    );
}
export default DirectorInputField;
